<template>
  <div
    :class="[
      'search-fact-finder-results-fi',
      headerHeightClasses,
      { 'search-fact-finder-results-fi--fite': isFite },
      { 'search-fact-finder-results-fi--no-sidebar': isWebsiteSearch },
    ]"
  >
    <slot
      :search-fact-finder-ng="searchFactFinderNg"
      :skip-search="skipSearch"
      name="heading"
    />

    <loading-indicator
      v-if="(searchFactFinderLoading || searchFactFinderNgBaseFacetsLoading) && !fetchingMore"
      variant="spinnerCentered"
    />

    <div
      v-if="isFite && facets.length && !showNoQueryHint && !skipSearch"
      class="search-fact-finder-results-fi__fite-filters"
    >
      <facets-menu-fi
        :facets="menuFacets"
        :allow-clear-all="true"
        clip
        @change="changeFacet"
        @clear-all-facets="clearAllFacets"
      >
        <template #facets="{ facet, open, change, close, toggleOpen, idx, registerDomElement }">
          <fact-finder-facet-fi
            :facet="facet"
            :open="open"
            :idx="idx"
            :register-dom-element="registerDomElement"
            @change="change"
            @toggle-open="toggleOpen(facet)"
            @close="close(facet)"
          />
        </template>
        <template #selection="{ facet, element }">
          {{ cleanName(facet.name) }}:
          <span
            v-if="facet.type === 'FLOAT' || facet.type === 'INTEGER'"
            class="facets-selection-fi__value"
          >
            {{ getLocalizedValue(element.selectedMinValue) }}
            [{{ facet.unit }}] -
            {{ getLocalizedValue(element.selectedMaxValue) }}
            [{{ facet.unit }}]
          </span>
          <span
            v-else
            class="facets-selection-fi__value"
            >{{ element.text }}</span
          >
        </template>
      </facets-menu-fi>
    </div>

    <div
      v-if="isFite && !isSearchPage && fiteCategories.length && !showNoQueryHint && !skipSearch"
      class="search-fact-finder-results-fi__fite-categories"
    >
      <fact-finder-categories
        :categories="fiteCategories"
        :filter-url-params="filterUrlParams"
        @toggle="onToggleCategory"
      />
    </div>

    <grid-container-fi
      :container="false"
      container-md
    >
      <template v-if="!isFite && !isWebsiteSearch && !showNoQueryHint">
        <fact-finder-categories-fi
          v-if="categories.length"
          :categories="categories"
          :filter-url-params="filterUrlParams"
        />

        <fact-finder-filters-fi
          v-if="facets.length"
          :filters="facets"
          :loading="searchFactFinderLoading"
          :fixed="!isMD && fixedFilter"
          @filter-changed="changeFacet"
          @reset-filter="clearAllFacets"
        />
      </template>

      <div
        v-if="
          !(searchFactFinderLoading || searchFactFinderNgBaseFacetsLoading) &&
          (showNoQueryHint || searchFactFinderNg?.totalHits === 0 || skipSearch)
        "
        :class="{
          'search-fact-finder-results-fi__no-results': true,
          'search-fact-finder-results-fi__no-results--no-query': showNoQueryHint,
        }"
      >
        <div
          v-if="showNoQueryHint"
          class="search-fact-finder-results-fi__query"
        >
          {{ $dict.get('search.Fact-Finder.No-Search-Result.Heading') }}
        </div>
        <fact-finder-no-results-fi
          :query="searchFactFinderNg?.query || ''"
          :no-filters="filters.length === 0"
          :is-category="isCategory"
          :show-result-suggestion="Boolean(showNoQueryHint)"
          @new-search="newSearch"
        />
      </div>

      <template v-else>
        <div
          v-if="isFite"
          class="search-fact-finder-results-fi__item-wrapper fi-col-span-full"
        >
          <div class="search-fact-finder-results-fi__item-wrapper-inner">
            <div
              class="search-fact-finder-results-fi__count"
              data-test="fact-finder-count"
              :data-test-2="familiesCount"
              :data-test-3="productsCount"
            >
              <span v-if="familiesCount && productsCount">
                {{
                  $dict.get('search.Families-And-Variants-Count', [
                    String(familiesCount),
                    String(productsCount),
                  ])
                }}
              </span>
              <span v-else-if="familiesCount">
                {{ $dict.get('search.Families-Count', [String(familiesCount)]) }}
              </span>
              <span v-else-if="productsCount">
                {{ $dict.get('search.Variants-Count', [String(productsCount)]) }}
              </span>
            </div>
            <div class="search-fact-finder-results-fi__item-list">
              <div
                :class="[
                  'search-fact-finder-results-fi__items',
                  { 'search-fact-finder-results-fi__items--website': isWebsiteSearch },
                ]"
              >
                <template
                  v-for="(record, recordIdx) in searchFactFinderNg?.masters?.hits
                    ? searchFactFinderNg?.masters.hits
                    : []"
                  :key="record?.idString"
                >
                  <fact-finder-item-fi
                    v-if="record"
                    :record="record"
                    :record-idx="recordIdx + (searchFactFinderNg?.masters?.hits?.length || 0)"
                    :filter-url="filterUrlParamsNoQuery"
                    :show-description="includeClaim"
                    :type="fischer.isProductSeriesEnabled() ? 'series' : 'family'"
                    @click="masterClicked(record)"
                  />
                </template>

                <fact-finder-item-fi
                  v-for="(record, recordIdx) in variantHits"
                  :key="record.variantValuesObject[0].ID"
                  :record="record"
                  :record-idx="recordIdx + (searchFactFinderNg.masters?.hits?.length || 0)"
                  :best-price="getBestPrice(record)"
                  :filter-url="filterUrlParamsNoQuery"
                  :show-description="includeClaim"
                  :type="isWebsiteSearch ? 'page' : 'product'"
                />
              </div>

              <div
                v-if="!isCategory"
                class="search-fact-finder-results-fi__pagination"
              >
                <a
                  v-if="previousPageUrlParam"
                  v-show="false"
                  :href="previousPageUrlParam"
                >
                  {{ $dict.get('search.Previous-Results') }}
                </a>
                <pagination-progress-bar
                  v-if="searchFactFinderNg?.totalHits"
                  :pagination-page="page"
                  :pagination-total="totalItems || 0"
                  :progressbar-value="pagingPercentage"
                  :disabled-state="searchFactFinderLoading"
                  :show-more-button="displayedItems < totalItems"
                  :query-params="filterUrlParams"
                  @loadmore="fetchMore"
                >
                  <template v-if="page * pageSize <= totalItems">
                    <span>{{ getPaginationString([page * pageSize, totalItems]) }}</span>
                  </template>

                  <template v-else>
                    <span>{{ getPaginationString([totalItems, totalItems]) }}</span>
                  </template>
                </pagination-progress-bar>
              </div>
            </div>
          </div>
        </div>

        <template v-else>
          <div
            class="search-fact-finder-results-fi__count"
            data-test="fact-finder-count"
            :data-test-2="familiesCount"
            :data-test-3="productsCount"
          >
            <span v-if="familiesCount && productsCount">
              {{
                $dict.get('search.Families-And-Variants-Count', [
                  String(familiesCount),
                  String(productsCount),
                ])
              }}
            </span>
            <span v-else-if="familiesCount">
              {{ $dict.get('search.Families-Count', [String(familiesCount)]) }}
            </span>
            <span v-else-if="productsCount">
              {{ $dict.get('search.Variants-Count', [String(productsCount)]) }}
            </span>
          </div>

          <div class="search-fact-finder-results-fi__item-list">
            <div
              v-if="!isMD && !isFite && !isWebsiteSearch && !showNoQueryHint"
              v-intersect="onIntersect"
            />

            <div
              :class="[
                'search-fact-finder-results-fi__items',
                { 'search-fact-finder-results-fi__items--website': isWebsiteSearch },
              ]"
            >
              <template
                v-for="(record, recordIdx) in searchFactFinderNg?.masters?.hits
                  ? searchFactFinderNg?.masters.hits
                  : []"
                :key="record?.idString"
              >
                <fact-finder-item-fi
                  v-if="record"
                  :record="record"
                  :record-idx="recordIdx + (searchFactFinderNg?.masters?.hits?.length || 0)"
                  :filter-url="filterUrlParamsNoQuery"
                  :show-description="includeClaim"
                  :type="fischer.isProductSeriesEnabled() ? 'series' : 'family'"
                  @click="masterClicked(record)"
                />
              </template>

              <template v-if="isFetchMoreCategories">
                <infinite-loading
                  v-if="isMounted"
                  ref="infiniteLoading"
                  class="search-fact-finder-results-fi__spinner"
                  @infinite="fetchMore"
                >
                  <template #spinner>
                    <div class="search-fact-finder-results-fi__spinner-tile">
                      <loading-indicator variant="spinnerLocal" />
                    </div>
                  </template>
                  <template #no-more><span /></template>
                  <template #no-results><span /></template>
                  <template #error><span /></template>
                </infinite-loading>

                <div
                  v-for="key in categoryPlaceholders"
                  :key
                  class="search-fact-finder-results-fi__spinner"
                >
                  <div class="search-fact-finder-results-fi__spinner-tile" />
                </div>
              </template>

              <fact-finder-item-fi
                v-for="(record, recordIdx) in variantHits"
                :key="record.variantValuesObject[0].ID"
                :record="record"
                :record-idx="recordIdx + (searchFactFinderNg.masters?.hits?.length || 0)"
                :best-price="getBestPrice(record)"
                :filter-url="filterUrlParamsNoQuery"
                :show-description="includeClaim"
                :type="isWebsiteSearch ? 'page' : 'product'"
              />

              <template v-if="!isCategory">
                <infinite-loading
                  v-if="infiniteLoadingAvailable && isMounted"
                  ref="infiniteLoading"
                  class="search-fact-finder-results-fi__spinner"
                  @infinite="fetchMore"
                >
                  <template #spinner>
                    <div class="search-fact-finder-results-fi__spinner-tile">
                      <loading-indicator variant="spinnerLocal" />
                    </div>
                  </template>
                  <template #no-more><span /></template>
                  <template #no-results><span /></template>
                  <template #error><span /></template>
                </infinite-loading>

                <div
                  v-for="key in placeholders"
                  :key
                  class="search-fact-finder-results-fi__spinner"
                >
                  <div class="search-fact-finder-results-fi__spinner-tile" />
                </div>
              </template>
            </div>

            <div
              v-if="!isCategory"
              class="search-fact-finder-results-fi__pagination"
            >
              <a
                v-if="previousPageUrlParam"
                v-show="false"
                :href="previousPageUrlParam"
              >
                {{ $dict.get('search.Previous-Results') }}
              </a>
              <pagination-progress-bar
                v-if="searchFactFinderNg?.totalHits"
                :pagination-page="page"
                :pagination-total="totalItems || 0"
                :progressbar-value="pagingPercentage"
                :disabled-state="searchFactFinderLoading"
                :show-more-button="displayedItems < totalItems"
                :query-params="filterUrlParams"
                @loadmore="fetchMore"
              >
                <template v-if="page * pageSize <= totalItems">
                  <span>{{ getPaginationString([page * pageSize, totalItems]) }}</span>
                </template>

                <template v-else>
                  <span>{{ getPaginationString([totalItems, totalItems]) }}</span>
                </template>
              </pagination-progress-bar>
            </div>
          </div>
        </template>
      </template>
    </grid-container-fi>
  </div>

  <slot
    name="withResults"
    :search-fact-finder-ng="searchFactFinderNg"
  />

  <div
    v-if="!isMD && !isFite && !isWebsiteSearch && !showNoQueryHint"
    v-intersect="onIntersectOut"
  />
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { LoadingIndicator, PaginationProgressBar } from 'atoms';
import { InfiniteLoading } from 'infinite-loading-vue3-ts';
import { storeToRefs } from 'pinia';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onServerPrefetch,
  provide,
  type Ref,
  ref,
  useTemplateRef,
  watch,
} from 'vue';

import FacetsMenuFi from '@/components/facets/facets-menu-fi/FacetsMenuFi.vue';
import type { ProcessedFactFinderNgFacet } from '@/components/facets/processedFacetTypes';
import type { Category as FiteCategory } from '@/components/search-fact-finder-fi/fact-finder-categories/FactFinderCategories.vue';
import FactFinderCategories from '@/components/search-fact-finder-fi/fact-finder-categories/FactFinderCategories.vue';
import { useAutoFiltersComposable } from '@/composables/autoFilters';
import { useBreakpoints } from '@/composables/breakpoint';
import { useEventHub } from '@/composables/eventHub';
import { useExponentialBackoff } from '@/composables/exponentialBackoff';
import { useFields } from '@/composables/fields';
import { useDict, useFischer } from '@/composables/fischerPlugin';
import { useHeaderHeight } from '@/composables/headerHeight';
import { useLocalizedValue } from '@/composables/localizedValue';
import { useBestPrices } from '@/composables/multiBestPrice';
import { useSalesChannelFilter } from '@/composables/salesChannelFilter';
import type { TrackingProductOrCategory } from '@/composables/tracking';
import { useTracking } from '@/composables/tracking';
import type {
  CategoryFacetElement,
  FactFinderNgFacetElementInput,
  FactFinderNgSearchRecord,
} from '@/generated/graphql';
import GET_SHOPIFY_PRODUCTS from '@/graphql/getShopifyProducts.gql';
import SEARCH_FACT_FINDER_NG from '@/graphql/searchFactFinderNg.gql';
import SEARCH_FACT_FINDER_NG_WEBSITE from '@/graphql/searchFactFinderNgWebsite.gql';
import { useNavigationStore } from '@/store/pinia/navigation';
import { useNotificationStore } from '@/store/pinia/notification';
import { useSearchStore } from '@/store/pinia/search';
import { useShopifyStore } from '@/store/pinia/shopify';

import GridContainerFi from '../grid-fi/GridContainerFi.vue';
import FactFinderFacetFi from '../search-fact-finder-fi/fact-finder-facet-fi/FactFinderFacetFi.vue';
import factFinderNgNameCleaner from '../search-fact-finder-fi/factFinderNgNameCleaner';
import mergeFacets from '../search-fact-finder-fi/mergeFacets';
import stringifyFilterValue from '../search-fact-finder-fi/stringifyFilterValue';
import technicalFacets from '../search-fact-finder-fi/technicalFacets';
import toFilterInput from '../search-fact-finder-fi/toFilterInput';
import FactFinderCategoriesFi from './fact-finder-categories-fi/FactFinderCategoriesFi.vue';
import FactFinderFiltersFi from './fact-finder-filters-fi/FactFinderFiltersFi.vue';
import FactFinderItemFi from './fact-finder-item-fi/FactFinderItemFi.vue';
import FactFinderNoResultsFi from './fact-finder-no-results-fi/FactFinderNoResultsFi.vue';

export interface SearchFactFinderSubCategory {
  Slug?: string;
  CategoryId?: string;
  CategoryName?: string;
  CategoryRole?: string;
  enabled?: boolean;
}

export interface SearchFactFinderCategory {
  Slug?: string;
  enabled?: boolean;
  id?: string;
  imageUrl?: string;
  name?: string;
  subCategories?: SearchFactFinderSubCategory[];
}

export interface SearchFactFinderFilter {
  associatedFieldName?: string;
  name?: string;
  selected?: boolean | null;
  selectedMinValue?: string;
  selectedMaxValue?: string;
}

export interface SearchFilter {
  value: boolean;
  element: SearchFactFinderFilter;
}

defineOptions({
  name: 'SearchFactFinderResultsFi',
});

const emits = defineEmits<{
  'total-hits': [value: number];
}>();

const props = defineProps<{
  categoryPathIdRoot?: string | null;
  clickSource: string;
  queryPrefix?: string | null;
  isSearchPage?: boolean;
  isWebsiteSearch?: boolean;
  isActive?: boolean;
  attachBreadcrumb?: boolean;
}>();

const fischer = useFischer();
const navigationStore = useNavigationStore();
const notificationStore = useNotificationStore();
const searchStore = useSearchStore();
const shopifyStore = useShopifyStore();
const { getLocalizedValue } = useLocalizedValue();
const { isMD } = useBreakpoints();
const cleanName = (name: string) => factFinderNgNameCleaner(name);
const {
  query: searchQuery,
  queryTrimmed: searchQueryTrimmed,
  suggestionsQuery: searchSuggestionsQuery,
} = storeToRefs(searchStore);
const dict = useDict();
const { headerHeightClasses } = useHeaderHeight();
const { sendEvent } = useEventHub();
const { withSalesChannelFilter, supplier, isPublicAssortment } = useSalesChannelFilter();

const fields = useFields('ProductCategoryFields');

const filters = ref([]);
const haveParsedUrlParams = ref(false);
const categoryOverride = ref<string | null>(null);
const page = ref(0);
const pageSize = ref(15);
const pageNonResponsive = ref(0);
const fetchingMore = ref();
const skipFacets = ref(false);
const lastTracked = ref(null);
const pastTopSentinel = ref(false);
const pastBottomSentinel = ref(false);
const fixedFilter = computed(() => pastTopSentinel.value && !pastBottomSentinel.value);

const isMounted = ref(false);
const observersReady = ref(false);

const infiniteLoadingRef = useTemplateRef('infiniteLoading');

const { trackViewItemList, trackSelectItem } = useTracking();
const { autoFilters, applyChanges, enableAutoFilters, useAutoFilters } =
  useAutoFiltersComposable(filters);

const getSearchQuery = computed(() =>
  props.isWebsiteSearch ? SEARCH_FACT_FINDER_NG_WEBSITE : SEARCH_FACT_FINDER_NG,
);

const query = computed(() => {
  if (props.categoryPathIdRoot) {
    return '';
  }
  return searchQuery.value || '';
});

const skipSearch = computed(() => {
  if (!props.isSearchPage) {
    return false;
  }
  return (
    isSuggestionsOpen.value ||
    showNoQueryHint.value ||
    trimmedQuery.value === '*' ||
    trimmedQuery.value === ''
  );
});

// The route mock holds one static value for every brand, so the showroom overrides it from
// the brand setting: set on FITE, whose products have no families, never on FIWE.
const variantsWithoutFamily = computed(() =>
  Boolean(IS_SHOWROOM ? fischer.brandId() === 'FITE' : fields.variantsWithoutFamily?.value),
);

const pagingPercentage = computed(() => (100 * displayedItems.value) / totalItems.value);

const infiniteLoadingAvailable = computed(
  () => totalItems.value && displayedItems.value < totalItems.value && !previousPageUrlParam.value,
);

const typeFilter = computed(() => (props.isWebsiteSearch ? 'page' : 'product'));

const isCategory = computed(() => {
  if (fischer.isProductSeriesEnabled()) {
    return Boolean(fischer.isCategoryPage() || fischer.isFamilyPage());
  }
  return Boolean(fischer.isCategoryPage());
});

const includeClaim = computed(() => Boolean(fields.showProductDescription?.value));

const familiesCount = computed(() => searchFactFinderNg.value?.totalFamilyHits || null);

const productsCount = computed(() => searchFactFinderNg.value?.variants?.totalHits || null);

const isSuggestionsOpen = computed(
  () =>
    navigationStore.showSearchSuggest || searchQueryTrimmed.value !== searchSuggestionsQuery.value,
);

const totalItems = computed(() =>
  isCategory.value
    ? searchFactFinderNg.value?.masters?.totalHits || 0
    : searchFactFinderNg.value?.totalHits || 0,
);

const displayedItems = computed(() =>
  Math.min(page.value * pageSize.value, searchFactFinderNg.value?.totalHits || 0),
);

const previousPageUrlParam = computed(() => {
  const pageParam = fischer.query('page');
  if (!pageParam) {
    return null;
  }
  const querPage = Number(pageParam);
  if (page.value !== querPage) {
    return null;
  }
  const previous = querPage - 1;
  if (!previous) {
    return null;
  }
  if (previous === 1) {
    return fischer.uri();
  }
  return `?page=${previous}`;
});

const isFetchMoreCategories = computed(
  () =>
    familiesCount.value &&
    searchFactFinderNg.value?.masters?.hits?.length < familiesCount.value &&
    !previousPageUrlParam.value,
);

const limitPlaceholders = (amount: number) => {
  let count = Math.max(0, amount);
  count = Math.min(250, count);
  return count;
};

const categoryPlaceholders = computed(() => {
  if (!isFetchMoreCategories.value) {
    return 0;
  }
  return limitPlaceholders(totalItems.value - displayedItems.value - 1);
});

const placeholders = computed(() => {
  if (!infiniteLoadingAvailable.value) {
    return 0;
  }
  return limitPlaceholders(totalItems.value - displayedItems.value - 1);
});

const showNoQueryHint = computed(() => props.isSearchPage && trimmedQuery.value === '');

const variantHits = computed(() => searchFactFinderNg.value?.variants?.hits || []);

const filterUrlParamsNoQuery = computed(() => {
  if (useAutoFilters.value) {
    return '';
  }

  const search = new URLSearchParams();

  const tab = fischer.query('tab');
  if (tab) {
    search.append('tab', tab);
  }

  const otherFiltersStart = props.isWebsiteSearch ? 'filter' : 'websitefilter';
  const queryObj = fischer.query();
  if (queryObj) {
    Object.entries(queryObj).forEach(([key, value]) => {
      if (key.startsWith(otherFiltersStart)) {
        search.append(key, value);
      }
    });
  }

  const prefix = props.isWebsiteSearch ? 'websitefilter' : 'filter';
  const searchFilters: any = [];

  filters.value.forEach((filter: SearchFactFinderFilter) => {
    if (!filter.associatedFieldName || filter.associatedFieldName === 'undefined') {
      console.error('filter has no associated field name', filter);
      return;
    }
    const activeFilter = searchFilters.find(
      (el: any) => el.associatedFieldName === filter.associatedFieldName,
    );
    if (activeFilter) {
      activeFilter.value = activeFilter.value + ',' + stringifyFilterValue(filter);
    } else {
      searchFilters.push({
        associatedFieldName: filter.associatedFieldName,
        value: stringifyFilterValue(filter),
      });
    }
  });

  searchFilters.forEach((filter: any) => {
    search.append(`${prefix}${filter.associatedFieldName}`, filter.value);
  });

  const searchString = search.toString();
  if (searchString.length === 0) {
    return '';
  }
  return `?${searchString}`;
});

const filterUrlParams = computed(() => {
  const q = query.value?.trim();
  if (!q) {
    return filterUrlParamsNoQuery.value;
  }
  const search = filterUrlParamsNoQuery.value ? `${filterUrlParamsNoQuery.value}&` : '?';
  return `${search}query=${encodeURIComponent(q)}`;
});

const categories = computed(() => {
  if (!isCategory.value) {
    return [];
  }

  const categoryFacetElements = searchFactFinderNgBaseFacets.value?.categoryFacets
    ?.filter(
      (facet: CategoryFacetElement) => facet?.catalogCategoryData?.CategoryRole === 'INTERMEDIATE',
    )
    .sort(
      (a: any, b: any) =>
        (a?.catalogCategoryData?.SortOrder || 1) - (b?.catalogCategoryData?.SortOrder || 0),
    );

  if (!categoryFacetElements || !searchFactFinderNg.value) {
    return [];
  }
  let currentFacets = searchFactFinderNg.value?.categoryFacets?.map(
    (facet: CategoryFacetElement) => facet?.text,
  );
  if (!currentFacets?.length) {
    const firstMaster = searchFactFinderNg.value?.masters?.hits?.[0]?.masterValuesObject;
    let firstHitPath;
    if (firstMaster?.catalogCategoryData) {
      firstHitPath = firstMaster.catalogCategoryData.CategoryDisplayNamePath;
    } else {
      firstHitPath = firstMaster?.masterProductData?.catalogProductData?.CategoryDisplayNamePath;
    }
    if (firstHitPath) {
      currentFacets = categoryFacetElements
        .filter((facet: CategoryFacetElement) =>
          firstHitPath.includes(facet?.catalogCategoryData?.CategoryName),
        )
        .map((facet: CategoryFacetElement) => facet?.text);
    } else {
      currentFacets = [];
      console.error('Could not find category id');
    }
  }

  return categoryFacetElements.map((filter: CategoryFacetElement) => {
    if (!filter) {
      return;
    }

    return {
      id: filter.text,
      name: filter.catalogCategoryData?.CategoryName,
      Slug: filter.catalogCategoryData?.Slug,
      imageUrl: filter.catalogCategoryData?.imageUrl,
      enabled:
        filter.text &&
        (currentFacets.includes(filter.text) ||
          searchFactFinderNg.value?.categoryFacetsUnselected?.includes(filter.text)),
    };
  }) as SearchFactFinderCategory[];
});

const trimmedQuery = computed(() => {
  if (props.isSearchPage) {
    return query.value.trim() || '';
  }

  return query.value.trim() || '*';
});

const graphQlVariables = computed(() => {
  let masterFilters: FactFinderNgFacetElementInput[] | undefined;
  let extraFilters: FactFinderNgFacetElementInput[] = [
    {
      associatedFieldName: 'Type',
      name: typeFilter.value,
    },
  ];

  if (!props.isSearchPage) {
    extraFilters.push({
      associatedFieldName: 'Hidden_K',
      name: '!true',
    });
  }
  if (!props.isWebsiteSearch) {
    extraFilters = withSalesChannelFilter(extraFilters);
    masterFilters = withSalesChannelFilter([]);
  }

  return {
    baseSiteId: fischer.baseSiteId(),
    brand: fischer.brandId(),
    language: fischer.catalogLocale(),
    filters: (autoFilters.value || filters.value).map(toFilterInput),
    quantity: isCategory.value ? 100 : pageSize.value,
    query: trimmedQuery.value,
    navigation: Boolean(props.categoryPathIdRoot),
    supplier: supplier(),
    // publicAssortment only ever restricts the master/family search (see the
    // gateway's masterOnlyFilters), never variants or facets.
    publicAssortment: isPublicAssortment(),
    extraFilters,
    masterFilters,
    categoryId: props.categoryPathIdRoot,
    catalogLocale: fischer.catalogLocale(),
    factFinderChannel: fischer.factFinderChannel(),
    categoryFacets: Boolean(fischer.isCategoryPage()),
    useSeries: fischer.isProductSeriesEnabled(),
  };
});

const searchFactFinderVars = computed(() => {
  return {
    ...graphQlVariables.value,
    categoryId: categoryOverride.value || graphQlVariables.value.categoryId,
    page: pageNonResponsive.value,
    noVariants: Boolean(props.categoryPathIdRoot),
    // Without families every master is one of the variants below it, so asking for both
    // lists the same products twice. Category listings render masters only and keep them.
    noMasters: variantsWithoutFamily.value && !props.categoryPathIdRoot,
    variantsWithoutFamily: variantsWithoutFamily.value,
    noFacets: skipFacets.value,
    includeClaim: includeClaim.value,
    originalCategoryId: graphQlVariables.value?.categoryId,
  };
});

const termToTrack = computed(() => {
  if (!props.isSearchPage || !props.isActive) {
    return null;
  }

  return searchFactFinderNg.value?.query || null;
});

const facets = computed(() => {
  const mergedFacets: any = mergeFacets(
    searchFactFinderNg.value?.facetDataUnselected || [],
    searchFactFinderNg.value?.facets || [],
    searchFactFinderNgBaseFacets.value?.facets || [],
    autoFilters.value || filters.value,
  );

  // make it possible to deselect autoFilters, even if no results found
  if (autoFilters.value?.length) {
    autoFilters.value.forEach((autoFilter) => {
      if (
        !mergedFacets.some(
          (facet: SearchFactFinderFilter) =>
            facet.associatedFieldName === autoFilter.associatedFieldName,
        )
      ) {
        mergedFacets.push({
          associatedFieldName: autoFilter.associatedFieldName,
          disabled: false,
          hidden: false,
          type: 'TEXT',
          name: autoFilter.associatedFieldName,
          unit: '',
          elements: [
            {
              disabled: false,
              associatedFieldName: autoFilter.associatedFieldName,
              text: autoFilter.fakeValue,
              name: autoFilter.name,
              selected: true,
              selectedBool: true,
              absoluteMaxValue: null,
              absoluteMinValue: null,
            },
          ],
        });
      }
    });
  }

  return mergedFacets;
});

const menuFacets = computed<ProcessedFactFinderNgFacet[]>(() =>
  facets.value.map((facet: ProcessedFactFinderNgFacet) => ({
    ...facet,
    elements: (facet.elements ?? []).map((element) => ({
      ...element,
      selected: element.selectedBool,
      name: element.text,
    })),
  })),
);

const fiteCategories = computed((): FiteCategory[] =>
  categories.value.map((cat) => ({
    id: cat.id ?? '',
    name: cat.name ?? '',
    Slug: cat.Slug ?? '',
    imageUrl: cat.imageUrl,
    enabled: cat.enabled ?? false,
    selected: categoryOverride.value === cat.id,
  })),
);

const onToggleCategory = (category: FiteCategory, selected: boolean) => {
  categoryOverride.value = selected ? category.id : null;
};

const getPageParam = () => {
  const queryParam = fischer.query('page');
  return Number(queryParam) || 1;
};

const changeFacet = async (...changes: any[]) => {
  applyChanges(changes, true);

  if (!IS_SSR) {
    let state = filterUrlParams.value;

    const qPage = fischer.query('page');
    if (qPage) {
      state = state
        ? `${state}&page=${encodeURIComponent(qPage)}`
        : `?page=${encodeURIComponent(qPage)}`;
    }

    const customQuery: [string?] = [];
    const query = fischer.query();
    const filterKeys = ['filter', 'query'];
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (!filterKeys.some((el) => key.startsWith(el))) {
          customQuery.push(`${key}=${encodeURIComponent(value)}`);
        }
      });
    }

    if (customQuery.length) {
      if (filterUrlParams.value) {
        state = `${filterUrlParams.value}&${customQuery.join('&')}`;
      } else {
        state = `?${customQuery.join('&')}`;
      }
    }

    if (state !== String(window.location.search)) {
      // we want to remove the page parameter here, because the state changed
      window.history.replaceState('', '', state || '?');

      page.value = 1;
      pageNonResponsive.value = 1;
    }
  }
};

const parseUrlParams = () => {
  if (haveParsedUrlParams.value) {
    return;
  }
  haveParsedUrlParams.value = true;

  const isCorrectTab = props.isWebsiteSearch === (fischer.query('tab') === 'website');

  const pageParam = isCorrectTab ? getPageParam() : 1; // remember NOW, since changeFacet will change it

  const technicalFacetsLowerCase = technicalFacets.map((name) => name.toLowerCase());
  const filters: SearchFilter[] = [];
  const search = fischer.query();
  const searchRegExp = props.isWebsiteSearch ? /^websitefilter(.*)/ : /^filter(.*)/;

  if (search) {
    Object.entries(search).forEach(([key, value]: any) => {
      const match = searchRegExp.exec(key);
      if (!match) {
        return;
      }

      const [, name] = match;
      const nameLowerCase = name.toLowerCase();
      if (!name || technicalFacetsLowerCase.includes(nameLowerCase)) {
        return;
      }

      const values = value.split(',');
      values.forEach((el: string) => {
        filters.push({
          element: {
            associatedFieldName: name,
            name: el,
            selected: true,
          },
          value: true,
        });
      });
    });
  }

  if (!props.categoryPathIdRoot) {
    const q = fischer.query('query');
    if (q && q !== query.value) {
      searchStore.setQuery({ value: q, noShowSuggestions: true });
      searchStore.setSearchSuggestions([], q);
    }
  }

  changeFacet(...filters);

  page.value = pageParam;
  pageNonResponsive.value = pageParam;
};

parseUrlParams();

// R5: main paginated search query
const searchPause = computed(() => skipSearch.value);
const {
  data: searchData,
  error: searchQueryError,
  fetching: searchFactFinderLoading,
  executeQuery: executeSearchQuery,
} = useQuery({
  query: getSearchQuery,
  variables: computed(() => searchFactFinderVars.value),
  pause: searchPause,
});

// R5: accumulation refs
const accumulatedMastersHits = ref<any[]>([]);
const accumulatedVariantsHits = ref<any[]>([]);
const latestSearchData = ref<any>(null);
const latestFacetData = ref<any>(null);

/** Store a search response, keeping the facet metadata of the search it belongs to. */
const setLatestSearchData = (raw: any) => {
  latestSearchData.value = raw;
  if (raw.facets) {
    latestFacetData.value = {
      facets: raw.facets,
      facetDataUnselected: raw.facetDataUnselected,
      categoryFacets: raw.categoryFacets,
      categoryFacetsUnselected: raw.categoryFacetsUnselected,
    };
  }
};

// Identifies the search inputs (NOT the page - paging appends, it must not invalidate). The
// accumulators are tagged with the inputs they were filled for, so staleness is derived instead of
// being wiped pre-emptively: a filter change that hits the urql cache is delivered synchronously,
// before any pre-flush watcher could clear anything, and no further update ever follows.
const searchInputsKey = computed(() =>
  JSON.stringify([query.value, filters.value, categoryOverride.value]),
);
const accumulatedInputsKey = ref<string | null>(null);
const accumulatedIsStale = computed(() => accumulatedInputsKey.value !== searchInputsKey.value);

// Stable per-hit keys (same identifiers the template uses as :key).
const masterHitKey = (h: any) => h?.idString;
const variantHitKey = (h: any) => h?.variantValuesObject?.[0]?.ID;
// Dedupe hits by key, keeping first occurrence (preserves order). A search result list must never
// show the same article twice, so this is a safe client-side guard. It is, however, a WORKAROUND:
// duplicates here mean the backend returned overlapping pages for `searchFactFinderNg` (observed:
// page 2 and page 3 returning the same products — a page→offset off-by-one in
// fischer-frontend-graphql, see PAGINATION_DUPLICATE_BUG_HANDOFF.md). We deliberately log loudly so
// this is never silently masked — if these errors appear, the backend pagination is still broken.
const dedupeHits = (hits: any[], keyOf: (h: any) => unknown, label: string): any[] => {
  const seen = new Set<unknown>();
  const droppedKeys: unknown[] = [];
  const result = hits.filter((h) => {
    const k = keyOf(h);
    if (k === undefined || k === null) return true;
    if (seen.has(k)) {
      droppedKeys.push(k);
      return false;
    }
    seen.add(k);
    return true;
  });
  if (droppedKeys.length) {
    console.error(
      `[SearchFactFinderResultsFi] Dropped ${droppedKeys.length} duplicate ${label} hit(s) while ` +
        `accumulating paginated results (query="${trimmedQuery.value}", page=${pageNonResponsive.value}). ` +
        'This means the GraphQL backend returned overlapping pages for searchFactFinderNg — ' +
        'the client dedupe is masking a backend pagination bug. Duplicate keys:',
      droppedKeys,
    );
  }
  return result;
};

// Holds the result whose post-processing had to be postponed because it arrived before mount -
// see the watcher below and applyResultEffects().
let pendingResultEffects: any = null;

// immediate + flush:'sync' - see createSsrExchange. immediate: a restored SSR result is
// delivered during setup, so a lazy watcher never sees it. sync: Vue drops non-sync watchers
// during SSR entirely, so without it this state is never filled on the server.
// immediate: an SSR-restored result is delivered synchronously during setup (see
// createSsrExchange), so a lazy watcher would never see it and the result list would stay empty.
watch(
  searchData,
  (d) => {
    if (!d) return;
    const raw = props.isWebsiteSearch ? d.searchFactFinderNgWebsite : d.searchFactFinderNg;
    if (!raw) return;

    const isStale = accumulatedIsStale.value;
    const isFetchMoreResult =
      !isStale && fetchingMore.value !== false && fetchingMore.value !== undefined;

    if (!isFetchMoreResult) {
      // Fresh search → replace accumulators.
      const freshMasters = dedupeHits(raw.masters?.hits ?? [], masterHitKey, 'master');
      const freshVariants = dedupeHits(raw.variants?.hits ?? [], variantHitKey, 'variant');
      // Guard: a result-less response (e.g. the separate base-facets request, which uses
      // includeResults:false) must not wipe an already-populated list mid-search. A result for
      // changed inputs always replaces, so a genuine 0-results search still clears the list.
      const hasResults = freshMasters.length > 0 || freshVariants.length > 0;
      if (hasResults || isStale) {
        setLatestSearchData(raw);
        accumulatedMastersHits.value = freshMasters;
        accumulatedVariantsHits.value = freshVariants;
        accumulatedInputsKey.value = searchInputsKey.value;
      }
    } else {
      // fetchMore → append, deduping so a re-fetched page can't duplicate existing hits.
      setLatestSearchData(raw);
      if (raw.masters?.hits?.length) {
        accumulatedMastersHits.value = dedupeHits(
          [...accumulatedMastersHits.value, ...raw.masters.hits],
          masterHitKey,
          'master',
        );
      }
      if (raw.variants?.hits?.length) {
        accumulatedVariantsHits.value = dedupeHits(
          [...accumulatedVariantsHits.value, ...raw.variants.hits],
          variantHitKey,
          'variant',
        );
      }
    }

    // Everything above only touches refs declared further up, so it is safe to run from setup and
    // the list renders straight from the restored payload. The follow-up effects are not: they
    // reach for fetchMoreBackoff/backoffTimer/loaderThrottleTimer and for updatePriceRequests,
    // which sits at the end of a computed chain fed by these very accumulators - all declared
    // below. Defer them to onMounted, which costs nothing: paging, the infinite loader and the
    // price requests are all post-paint concerns anyway.
    if (isMounted.value) {
      applyResultEffects(raw);
    } else {
      pendingResultEffects = raw;
    }
  },
  { immediate: true, flush: 'sync' },
);

/** Post-processing for a search result. Only ever called after mount - see the watcher above. */
const applyResultEffects = (raw: any) => {
  fetchMoreBackoff.reset();
  updatePriceRequests();

  if (fetchingMore.value) {
    clearTimeout(backoffTimer);
    const loaded = fetchingMore.value;
    fetchingMore.value = false;
    loaderThrottleTimer = setTimeout(loaded, 1000); // throttle requests
    return;
  }

  if (!fischer.query('page')) {
    page.value = 1;
    pageNonResponsive.value = 1;
    skipFacets.value = false;
    if (infiniteLoadingRef.value?.stateChanger) {
      infiniteLoadingRef.value.stateChanger.reset();
    }
  }

  if (raw.masters?.hits) {
    const trackingList = raw.masters.hits.map(
      (hit: FactFinderNgSearchRecord) =>
        hit.masterValuesObject?.catalogCategoryData ||
        hit.masterValuesObject?.masterProductData?.catalogProductData,
    );
    trackViewItemList(trackingList);
  }
};

onServerPrefetch(async () => {
  if (searchPause.value) return;
  await executeSearchQuery();
});

// R1: base facets query (non-paginated, SEO-relevant)
const baseFacetsPause = computed(() => props.isWebsiteSearch || skipSearch.value);
const baseFacetsVariables = computed(() => ({
  ...graphQlVariables.value,
  filters: [],
  query: trimmedQuery.value,
  includeResults: false,
  noSearch: true,
  variantsWithoutFamily: variantsWithoutFamily.value,
  includeUnselectedFacets: false,
  accessToken: null,
}));
const {
  data: baseFacetsData,
  fetching: searchFactFinderNgBaseFacetsLoading,
  executeQuery: executeBaseFacetsQuery,
} = useQuery({
  query: SEARCH_FACT_FINDER_NG,
  variables: baseFacetsVariables,
  pause: baseFacetsPause,
});

onServerPrefetch(async () => {
  if (baseFacetsPause.value) return;
  await executeBaseFacetsQuery();
});

const searchFactFinderNgBaseFacets = computed(() => baseFacetsData.value?.searchFactFinderNg);

// R5: accumulated searchFactFinderNg computed — combines latest metadata with accumulated hits
const searchFactFinderNg = computed(() => {
  // Hidden while the accumulators still belong to the previous inputs, so a search in flight shows
  // its loader instead of the outgoing result set.
  if (!latestSearchData.value || accumulatedIsStale.value) return null;
  return {
    ...latestSearchData.value,
    // re-attach the facets of the search when the latest response is a facet-less fetchMore page
    ...(latestSearchData.value.facets ? {} : latestFacetData.value),
    masters: latestSearchData.value.masters
      ? { ...latestSearchData.value.masters, hits: accumulatedMastersHits.value }
      : undefined,
    variants: latestSearchData.value.variants
      ? { ...latestSearchData.value.variants, hits: accumulatedVariantsHits.value }
      : undefined,
  };
});

const hasShopifyProducts = computed(
  () =>
    searchFactFinderNg.value?.masters?.hits?.some((hit: any) =>
      hit?.masterValuesObject?.masterProductData?.catalogProductData?.Attributes?.find(
        (attribute: any) => attribute.AttributeKey === 'ShopifyID',
      ),
    ) ?? false,
);

// R1: Shopify products query (non-paginated, client-only — gated behind hasShopifyProducts)
const { data: shopifyData, fetching: shopifyQueryLoading } = useQuery({
  query: GET_SHOPIFY_PRODUCTS,
  variables: computed(() => ({ first: 100 })),
  pause: computed(
    () => !(fischer.app.isFite() && fields.enableShopify?.value && hasShopifyProducts.value),
  ),
});

// immediate + flush:'sync' - see createSsrExchange. immediate: a restored SSR result is
// delivered during setup, so a lazy watcher never sees it. sync: Vue drops non-sync watchers
// during SSR entirely, so without it this state is never filled on the server.
// immediate: if this result ever comes back restored from the SSR payload it is delivered during
// setup, and a lazy watcher would never see it (see createSsrExchange). No-ops when undefined.
watch(
  shopifyData,
  (d) => {
    if (d?.getShopifyProducts?.products?.edges) {
      shopifyStore.shopifyProducts = d.getShopifyProducts.products.edges;
    }
  },
  { immediate: true, flush: 'sync' },
);

watch(shopifyQueryLoading, (loading) => {
  shopifyStore.loading = loading;
});

const productIds = computed(() => {
  const ids: string[] = [];
  if (!props.isWebsiteSearch && searchFactFinderNg.value?.variants?.hits) {
    searchFactFinderNg.value.variants.hits.forEach((variant: any) => {
      const id = variant?.variantValuesObject?.[0]?.ID;
      if (id) {
        ids.push(id);
      }
    });
  }
  return ids;
});

const { updatePriceRequests, bestPrices } = useBestPrices(productIds);

const clearAllFacets = () => {
  enableAutoFilters();
  filters.value = [];
  changeFacet();
};

const newSearch = (search: string) => {
  clearAllFacets();
  searchStore.setQuery(search);
};

const getPaginationString = (obj: number[]) => {
  return dict.get(
    props.isSearchPage || variantsWithoutFamily.value
      ? 'product-List.Pagination.Show-Sum'
      : 'product-List.Pagination.Show-Families',
    [String(obj[0]), String(obj[1])],
  );
};

const masterClicked = (record: FactFinderNgSearchRecord) => {
  if (record?.masterValuesObject?.catalogCategoryData) {
    trackSelectItem(record.masterValuesObject.catalogCategoryData as TrackingProductOrCategory);
  }
};

const getBestPrice = (record: FactFinderNgSearchRecord) => {
  if (!fischer.shop.isClosedShop || !fischer.shop.isOnlineShop) {
    return null;
  }

  const id = record?.variantValuesObject?.[0]?.ID;
  const bestPrice = bestPrices.value?.[id as keyof object];
  if (bestPrice !== undefined) {
    return bestPrice;
  }
  return {
    price: {
      loading: true,
    },
  };
};

const fetchMoreBackoff = useExponentialBackoff();
let backoffTimer: ReturnType<typeof setTimeout> | undefined;
// complete() of the infinite-loader page currently in flight, handed over by fetchMore()
// so the error watcher can stop the loader after too many consecutive failures.
let pendingFetchMoreComplete: (() => void) | undefined;
let loaderThrottleTimer: ReturnType<typeof setTimeout> | undefined;
let isUnmounted = false;

onBeforeUnmount(() => {
  isUnmounted = true;
  clearTimeout(backoffTimer);
  clearTimeout(loaderThrottleTimer);
});

const fetchMore = async ({ complete, loaded }: any = {}) => {
  if (fetchingMore.value) {
    return;
  }
  if (page.value * pageSize.value > (searchFactFinderNg.value?.totalHits || 0)) {
    if (complete) {
      complete();
    }
    return;
  }
  fetchingMore.value = loaded || (() => null);
  skipFacets.value = true;
  page.value += 1;
  pageNonResponsive.value = page.value;
  // urql auto-re-runs because searchFactFinderVars computed uses pageNonResponsive.value.
  // loaded() is called inside watch(searchData, ...) when data arrives. There is no
  // promise to .catch() here as there was with Apollo's fetchMore, so a failed page
  // surfaces via the query's `error` ref -- the retry/backoff lives in the
  // watch(searchQueryError, ...) below, which needs this page's complete() callback.
  pendingFetchMoreComplete = complete;
};

// Paging reset on input change. It must NOT clear the accumulators or latestSearchData: a filter
// change that hits the urql cache is delivered by the sync searchData watcher before this pre-flush
// watcher runs, so clearing here would throw that result away and nothing would ever re-deliver it
// (blank filters and results after unselecting a facet). Staleness is derived from
// accumulatedInputsKey instead, which also keeps a fetchMore in flight from appending to the
// results of the previous inputs.
watch(
  () => [query.value, filters.value, categoryOverride.value] as const,
  () => {
    fetchingMore.value = false;
    skipFacets.value = false;
    page.value = 1;
    pageNonResponsive.value = 1;
  },
  { deep: true },
);

// urql resolves with an `error` instead of rejecting, so this watcher performs the
// rollback + exponential backoff that used to hang off fetchMore()'s promise.
watch(searchQueryError, (error) => {
  if (!error || isUnmounted) {
    return;
  }
  console.error(error);
  if (!fetchingMore.value) {
    return;
  }
  const complete = pendingFetchMoreComplete;
  page.value -= 1;
  pageNonResponsive.value = page.value;
  const loadedCallback = fetchingMore.value;
  const delay = fetchMoreBackoff.recordFailure();
  if (delay === null) {
    // too many consecutive failures: stop the infinite loader for this view
    fetchingMore.value = false;
    notificationStore.openGeneralErrorNotification();
    if (complete) {
      complete();
    }
    return;
  }
  // hold the loader during the backoff window; fetchingMore gates re-entry
  backoffTimer = setTimeout(() => {
    fetchingMore.value = false;
    if (typeof loadedCallback === 'function') {
      loadedCallback();
    }
  }, delay);
});

const makeIntersectHandler = (sentinel: Ref<boolean>) => (observer: any) => {
  if (
    IS_SSR ||
    window.scrollY === 0 ||
    !observersReady.value ||
    searchFactFinderLoading.value ||
    searchFactFinderNgBaseFacetsLoading.value ||
    fetchingMore.value
  )
    return;

  sentinel.value = Boolean(
    !observer?.[0]?.isIntersecting && observer[0]?.target?.getBoundingClientRect?.()?.top <= 0,
  );
};

const onIntersect = makeIntersectHandler(pastTopSentinel);
const onIntersectOut = makeIntersectHandler(pastBottomSentinel);

watch(
  () => query.value,
  () => {
    changeFacet();
  },
);

watch(
  () => termToTrack.value,
  (newValue) => {
    if (!newValue) {
      return;
    }
    if (lastTracked.value === newValue) {
      return;
    }
    lastTracked.value = newValue;

    sendEvent('search', {
      searchTerm: newValue,
      searchContext: props.isWebsiteSearch ? 'website' : 'product',
    });
  },
);

watch(
  () => searchFactFinderNg.value,
  (newValue) => {
    if (newValue?.totalHits) {
      emits('total-hits', newValue?.totalHits);
    }
  },
);

watch(
  () => filters.value,
  () => {
    if (skipSearch.value) {
      return;
    }

    try {
      executeSearchQuery();
    } catch (err) {
      console.error(err);
    }
  },
  { deep: true },
);

watch(
  () => isSuggestionsOpen.value,
  (newValue) => {
    if (!props.isWebsiteSearch) {
      return;
    }

    if (!newValue) {
      clearAllFacets();
      try {
        executeSearchQuery();
      } catch (err) {
        console.error(err);
      }
    }
  },
);

onMounted(() => {
  isMounted.value = true;
  // A result restored from the SSR payload reached the watcher during setup, too early for these
  // effects to run. Replay them now that every declaration they touch exists.
  if (pendingResultEffects) {
    const raw = pendingResultEffects;
    pendingResultEffects = null;
    applyResultEffects(raw);
  }
  nextTick(() => {
    observersReady.value = true;
  });
});

provide('getSearchData', () => ({
  query: props.queryPrefix ? props.queryPrefix : searchFactFinderNg.value?.query || '',
  pageSize: pageSize.value || 1,
  attachBreadcrumb: props.attachBreadcrumb,
  clickSource: props.clickSource,
}));

defineExpose({
  searchFactFinderNg,
  searchFactFinderLoading,
  parseUrlParams,
});
</script>

<style scoped src="./search-fact-finder-results-fi" />
